import { Component, EventEmitter, input, Input, OnInit, output, Output } from '@angular/core';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { BuyerDealerResult, Organization, OrganizationService, OrganizationState } from 'remarketing-angular-library';
import { Observable } from 'rxjs';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { Store } from '@ngxs/store';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { get } from 'lodash';

@Component({
  selector: 'app-search-box-input',
  imports: [ TranslocoDirective,
    AsyncPipe,
    FormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule],
  templateUrl: './search-box-input.component.html',
  styleUrl: './search-box-input.component.scss'
})
export class SearchBoxInputComponent extends SaComponent implements OnInit  {
  isDealerSeller$ = input<boolean>(false);

  filteredDealers: Observable<BuyerDealerResult[]>;
  buyerOeSearchPhraseControl = new FormControl<BuyerDealerResult | string>('');
  searchResults: BuyerDealerResult[] = [];
  buyerOeSearchPhrase = '';
  dropDownFocusTrap = false;
  searchFocus = false;
  hideDropDown = true;

  dealerSelected = output<BuyerDealerResult>();

  searchBoxInputTranslations = {
    textSearch: '',
    textSearchDealership: '',
  };

  constructor(
    private readonly organizationService: OrganizationService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getTranslations();
    this.setFilteredDealers();
    this.buyerOeNameSearch();
  }

  setFilteredDealers() {
    this.filteredDealers = this.buyerOeSearchPhraseControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterDealers(value)),
      takeUntil(this.destroyed$)
    );
  }

  displayDealerFn(dealer: any): string {
    return dealer ? dealer.oeDetails ?? dealer : '';
  }

  private _filterDealers(value): BuyerDealerResult[] {
    const filterValue = typeof value === 'string' 
      ? value.toLowerCase() 
      : value?.oeName?.toLowerCase() ?? '';
    return this.searchResults.filter(dealer => dealer.oeName.toLowerCase().includes(filterValue));
  }

  searchFocusOnBlur() {
    this.searchFocus = false;
  }

  clearBuyerOeSearchPhrase() {
    this.buyerOeSearchPhraseControl.setValue('');
  }

  buyerOeNameSearch() {
    this.searchResults = [];
    this.isDealerSeller$() ? this.getPartialBuyerOeNamesFromOrganization() : this.getPartialBuyerOeNamesFromDealerGroupMembers();
  }

  getPartialBuyerOeNamesFromOrganization() {
    this.organizationService
    .getBuyerOrganizationByPartialOeName('')
    .pipe(take(1))
    .subscribe((organizations: Organization[]) => {
      this.parseSearchResults(organizations);
    });
  }

  getPartialBuyerOeNamesFromDealerGroupMembers(){
    const acctMgrGrpUuid =  get(this.store.snapshot(), 'user.userProfile.acctMgrGrpUuid', null);
    // remove acctMgrGrpUuid check if wanting error screen when sending null to organization service
    // added check to get modal showing in SIM V2 development
    if(acctMgrGrpUuid !== null) {
      this.organizationService.getDealerGroupMembers(acctMgrGrpUuid)
      .pipe(take(1))
      .subscribe((organizations: Organization[]) => {
        this.parseSearchResults(organizations);
      });
    } else {
      console.log('User is missing valid data to get dealer group members');
    }
  }

  parseSearchResults(organizations: Organization[]){
    const org = this.store.selectSnapshot(OrganizationState.organizationState);
    organizations
    .filter(o => o.oeUuid !== org.organization.oeUuid)
    .forEach(org => {
      this.searchResults.push({
        oeDetails: [org.oeName, org.physicalAddress.city, org.physicalAddress.state].filter(val => !!val).join(', '),
        oeUuid: org.oeUuid,
        oeName: org.oeName
      });
    });
    this.buyerOeSearchPhraseControl.setValue(this.buyerOeSearchPhraseControl.getRawValue());
  }

  onDealerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedDealer = this.searchResults.find(x => x.oeUuid === event.option.value.oeUuid);
    if (selectedDealer) {
      this.dealerSelected.emit(selectedDealer);
      this.buyerOeSearchPhraseControl.setValue(selectedDealer);
    }
  }

  getTranslations() {
    const keyParams = {
      'common': {},
      'offerList': {}
    };

    this.translocoService.selectTranslateObject(keyParams).pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.searchBoxInputTranslations.textSearch = translate[0].labelSearch;
      this.searchBoxInputTranslations.textSearchDealership = translate[1].general.searchDealership;
    });
  }
}
